// What the film's two drawn layers say and where they sit, shared by the
// film's overlays (Film.tsx, burned into demo.mp4 by build-cut.sh) and the
// landing page's loop, which draws them over the clean picture itself
// (src/components/LoopNotes.tsx), so that it can keep them clear of its nav
// and headline on whatever screen it is on. No imports: the site's bundle
// takes this file as it is.

export const FPS = 24;

// ── Shot 5: her call ─────────────────────────────────────────────────────
// The times are the clip's own, from its audio (faster-whisper, word
// timestamps). Ana's greeting starts 3.1s before the cut, at 0:16.9 of the
// film against a cut at 0:20.0, so it is most of the way down when the card
// opens and her reply lands in the hole it leaves.
export type Word = [text: string, start: number, end: number];
export const ANA_AT = -3.1;
export const ANA: Word[] = (
  [
    ["Hi,", 0.0, 0.36], ["you've", 0.54, 0.7], ["reached", 0.7, 0.88], ["the", 0.88, 1.04], ["hire", 1.04, 1.2], ["company.", 1.2, 1.58],
    ["Is", 2.18, 2.42], ["this", 2.42, 2.62], ["about", 2.62, 2.9], ["an", 2.9, 3.12], ["existing", 3.12, 3.48], ["booking", 3.48, 3.86],
    ["or", 3.86, 4.32], ["something", 4.32, 4.7], ["new?", 4.7, 5.02],
  ] as Word[]
).map(([w, a, b]) => [w, a + ANA_AT, b + ANA_AT]);
export const HER: Word[] = [
  ["It's", 1.64, 2.12], ["the", 2.12, 2.24], ["booking", 2.24, 2.44], ["for", 2.44, 2.8], ["James's", 2.8, 3.3], ["farewell", 3.3, 3.6], ["party.", 3.6, 4.12],
  ["I", 4.32, 4.4], ["forgot", 4.4, 4.62], ["to", 4.62, 4.86], ["add", 4.86, 5.1], ["the", 5.1, 5.38], ["trestle", 5.38, 6.78], ["tables.", 6.78, 7.14],
  ["Can", 7.4, 7.5], ["we", 7.5, 7.62], ["add", 7.62, 7.8], ["six?", 7.8, 8.04],
];

// How much voice there is at t: 1 inside a word, falling off over 0.15s.
export function voice(t: number) {
  let v = 0;
  for (const [, a, b] of [...ANA, ...HER]) {
    if (t >= a && t <= b) return 1;
    const d = t < a ? a - t : t - b;
    v = Math.max(v, 1 - d / 0.15);
  }
  return Math.max(v, 0);
}

// The node beside her head and the card on its leader, at the film's size
// (1920x1080). The card's entrance: the node lands on frame 20, the leader
// draws, the card grows from its end. The loop's seam is at frame 18 of this
// shot, so the whole entrance plays after it.
export const NODE = { x: 1062, y: 470 };
export const CARD5 = { x: 1236, y: 296, w: 560 };

// ── Shot 6: the crew find out ────────────────────────────────────────────
// The alert is at 0.4s of the shot. The node rides the phone on the dash
// (track.ts) and the leader runs up to the middle of the banner's bottom
// edge, which sits at BANNER.y + BANNER_FOOT.
export const ALERT = Math.round(0.4 * FPS);
export const BANNER = { x: 190, y: 618, w: 470 };
export const BANNER_FOOT = 152;
export const MAIL = {
  from: "HireOS",
  subject: "Enquiry update: James's farewell party",
  // Nothing is changed until the team says so: the receptionist never
  // confirms a change herself.
  body: "Hannah rang at 2:46 pm asking to add six trestle tables to the booking. The change is waiting for you to accept.",
  // The loop's: it is up for two seconds over a moving cab, and nobody
  // reads a paragraph in that.
  short: "Six trestle tables, waiting for you to accept.",
};

// ── The tall loop ────────────────────────────────────────────────────────
// The phone's loop is the clean wide one widened to 720x1280 by fal's LTX
// 2.3 Reframe, which kept the film's frame whole as a band across the
// middle (1920 wide scales by 0.375 to 720; its top at row 437.5), then
// cropped to TALL.width around each shot by build-loop-tall.sh.
export const TALL = { width: 590, height: 1280 };
export const TALL_CROP = { shot5: 7, shot6: 130 };
export const tallX = (x: number, crop: number) => x * 0.375 - crop;
export const tallY = (y: number) => y * 0.375 + 437.5;
